/**
 * Pure HUD readability spec (LOCKED-DESIGN 18.8): generalizes the
 * ArenaReadability pattern to the DOM chrome. The browser QA harness collects
 * a HudSnapshot from `[data-hud-element]` rects and evaluates it here, and the
 * unit suite keeps every rule red-capable with synthetic fixtures.
 *
 * Rules are intent-level so the Phase 2-4 restyle can move, restyle, and
 * reword chrome freely as long as it stays present, on-screen, and unstacked.
 *
 * What counts as chrome: readable elements. Conditional chrome (toast,
 * world-anchored tags, alert, baffle-gate) carries data-hud-element and obeys
 * the safe-area/overlap rules WHENEVER visible, but only always-present
 * chrome joins REQUIRED_HUD_ELEMENTS. Full-frame effect layers (the flash
 * white-out, the alarm/pressure vignette) are frame treatments, not chrome —
 * they deliberately carry NO data-hud-element because an inset:0 element
 * violates the safe-area and overlap rules by construction.
 */

export type HudElementRect = {
  /** stable semantic id carried by the data-hud-element attribute */
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type HudState = "tutorial" | "playing" | "simple-playing";

export type HudSnapshot = {
  viewport: { width: number; height: number };
  state: HudState;
  /** every VISIBLE chrome element (hidden/zero-size elements are omitted) */
  elements: HudElementRect[];
};

/** Chrome may sit close to the frame edge, but never bleed past it. */
export const HUD_SAFE_MARGIN_PX = 8;

/**
 * Chrome that must be readable for each game state (always-present only).
 *
 * `ticket` (dispatch panel), `load-dock` (reticle dock), `shift-telegraph`, and
 * `baffle-gate` are CONDITIONAL chrome — they hide cleanly when there is no
 * active ticket / held load / imminent shift / hazard phase (LOCKED-DESIGN 9,
 * 18.5, 18.10), so they carry data-hud-element and obey the safe-area/overlap
 * rules whenever visible but are not required here. `chain` joins the required
 * set: the Chain ladder is always visible (Section 8.2).
 *
 * `simple-playing` is the Simple/kid HUD (LOCKED-DESIGN 18.11): the sim still
 * computes Grade / Chain / dispatch / shift, but Simple HIDES them, so the
 * required set drops to the five surfaced elements — score, timer, the BINS
 * (overflow) meter, hint, and pause. The `tutorial` and `playing` sets are
 * unchanged so qa:arena's adult flow is untouched.
 */
export const REQUIRED_HUD_ELEMENTS: Record<HudState, readonly string[]> = {
  tutorial: ["tutorial-card", "tutorial-skip", "hint", "pause"],
  playing: ["score", "timer", "overflow", "grade", "phase", "chain", "hint", "pause"],
  "simple-playing": ["score", "timer", "overflow", "hint", "pause"],
};

export function hudReadabilityIssues(snapshot: HudSnapshot): string[] {
  const issues: string[] = [];
  const present = new Set(snapshot.elements.map((element) => element.id));
  for (const id of REQUIRED_HUD_ELEMENTS[snapshot.state]) {
    if (!present.has(id)) issues.push(`${id} is missing while ${snapshot.state}`);
  }
  for (const element of snapshot.elements) {
    if (
      element.left < HUD_SAFE_MARGIN_PX || element.top < HUD_SAFE_MARGIN_PX ||
      element.right > snapshot.viewport.width - HUD_SAFE_MARGIN_PX ||
      element.bottom > snapshot.viewport.height - HUD_SAFE_MARGIN_PX
    ) issues.push(`${element.id} leaves the viewport safe area`);
  }
  for (let a = 0; a < snapshot.elements.length; a++) {
    for (let b = a + 1; b < snapshot.elements.length; b++) {
      const first = snapshot.elements[a]!;
      const second = snapshot.elements[b]!;
      if (first.left < second.right && second.left < first.right && first.top < second.bottom && second.top < first.bottom) {
        issues.push(`${first.id} overlaps ${second.id}`);
      }
    }
  }
  return issues;
}
